import type { Resource } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from '@eluve/fhir-utils';

/**
 * The uniform "human review state of an AI-proposed resource" meta.tag —
 * THE single declaration (ADR 0002/0003). Every producer (mini-app agents,
 * care-plan pending changes) and every consumer (review panels, profile
 * drawers, tenant tables) imports these; nothing mirrors the strings.
 *
 * FHIR has no shared verification field across resource types, so this tag is
 * the cross-type review index. It is never the source of truth for clinical
 * state — the resource's native field (status / verificationStatus /
 * lifecycleStatus) carries that; the tag only answers "where is this in
 * review" with a single tag-scoped search.
 */
export const REVIEW_STATUS_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/review-status`;

/** Awaiting human review; native state is the type's draft form. */
export const REVIEW_STATUS_PENDING = 'pending';
/** Human approved verbatim → native field confirmed/final/accepted. */
export const REVIEW_STATUS_ATTESTED = 'attested';
/** Human corrected the content and thereby approved it — confirmed like
 *  `attested`, but surfaced distinctly as an edited (corrected) approval. */
export const REVIEW_STATUS_EDITED = 'edited';
/** Human rejected → native field entered-in-error (never deleted). */
export const REVIEW_STATUS_REJECTED = 'rejected';
/** Retracted by a later agent re-run (ADR 0002). Reserved: defined here so
 *  readers can match it, but no producer writes it yet. */
export const REVIEW_STATUS_SUPERSEDED = 'superseded';

export type ReviewStatusCode =
  | typeof REVIEW_STATUS_PENDING
  | typeof REVIEW_STATUS_ATTESTED
  | typeof REVIEW_STATUS_EDITED
  | typeof REVIEW_STATUS_REJECTED
  | typeof REVIEW_STATUS_SUPERSEDED;

/** The resource's review-status tag code, or undefined when untagged
 *  (human-authored resources never carry the tag). */
export function readReviewStatus(resource: Resource): string | undefined {
  return resource.meta?.tag?.find((tag) => tag.system === REVIEW_STATUS_SYSTEM)
    ?.code;
}

/**
 * Whether a review-status code counts as human-approved. Accepts `null` and
 * `undefined` alike because the two readers in the codebase disagree on which
 * they return for an untagged resource, and callers must not have to care.
 * Untagged is NOT confirmed — decide that case explicitly at the call site.
 */
export function isConfirmedReviewStatus(
  status: string | null | undefined,
): boolean {
  return status === REVIEW_STATUS_ATTESTED || status === REVIEW_STATUS_EDITED;
}

/**
 * Whether the resource is in the review lifecycle at all. False means nothing
 * ever proposed it for review — a plan-native, template-compiled row — which is
 * NOT the same as an unreviewed proposal, though both read as `pending`.
 */
export function hasReviewStatusTag(resource: Resource): boolean {
  return readReviewStatus(resource) !== undefined;
}

/** Whether the resource carries `review-status: pending`. Callers layer any
 *  per-type native-state checks (e.g. a bare `provisional` Condition written
 *  outside the agent pipeline) on top of this tag predicate. */
export function hasPendingReviewTag(resource: Resource): boolean {
  return readReviewStatus(resource) === REVIEW_STATUS_PENDING;
}

/**
 * Replace the `review-status` meta.tag, preserving every other tag — the one
 * transition helper every reviewer surface uses, so pending → attested /
 * edited / rejected flips are shape-identical everywhere.
 */
export function setReviewStatusTag<T extends Resource>(
  resource: T,
  code: string,
): T {
  const others = (resource.meta?.tag ?? []).filter(
    (tag) => tag.system !== REVIEW_STATUS_SYSTEM,
  );
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [...others, { system: REVIEW_STATUS_SYSTEM, code }],
    },
  };
}
